import {DownloadOutlined, EditOutlined, LoadingOutlined} from '@ant-design/icons/lib';
import {Button, Card, Divider, Modal, Typography} from 'antd';
import {
  AccountStatus,
  BillingPlans,
  ContractStatus,
  StatusReason,
  UpdateAccountInput,
  useAccountEventsQuery,
  useAccountLazyQuery,
  useAccountQuery,
  useAccountTagsQuery,
  useDownloadContractMutation,
  useMasterAccountQuery
} from 'api/generated';
import {AnchorLink, PropertyList, Stack} from 'components';
import {AccountTag} from 'components/AccountTags';
import {Formik} from 'formik';
import {Form, FormItem, Input, Mentions, Select, SubmitButton, Switch} from 'formik-antd';
import {useAccessLevel} from 'hooks/useAccessLevel';
import {useAdminUsers} from 'hooks/useAdminUsers';
import {hasStatusReason} from 'lib/accountUtils';
import {getAccountTags} from 'lib/acountTagUtils';
import {formatDate, formatAffiliateReferralCode} from 'lib/formaters';
import {accountStatusLabels, statusReasonLabels} from 'lib/labels';
import {enumKeys} from 'lib/utils';
import Linkify from 'linkify-react';
import moment from 'moment';
import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {getChurnReason} from 'routes/Account/utils';
import {SectionProps} from './types';
import {useAffiliates} from 'hooks/useAffiliates';

const {Option} = Select;
const {TextArea} = Input;
const {Text} = Typography;

export const AccountDetails: React.FC<SectionProps> = ({
  cardProps,
  formProps,
  account,
  onSubmit
}) => {
  const [isEditing, setEditing] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [downloadContract, {loading: isDownloading}] = useDownloadContractMutation();
  const {isAdmin, isMaster} = useAccessLevel();
  const {selectOptions: affiliates, loading: affiliatesLoading} = useAffiliates();
  const {data: accountEventsData, loading} = useAccountEventsQuery({
    variables: {accountId: account?.id!},
    skip: !account || !isAdmin
  });
  const {data: {masterAccount} = {}} = useMasterAccountQuery({
    variables: {id: account?.masterAccountId!},
    skip: !account?.masterAccountId || !isAdmin,
    context: {useApolloNetworkStatus: false}
  });

  const adminUsers = useAdminUsers();

  const {data: accountTagsData} = useAccountTagsQuery({skip: !isAdmin});

  const {data: {account: refAccount} = {}} = useAccountQuery({
    variables: {id: account?.refAccountId!},
    skip: !account?.refAccountId || !isAdmin,
    context: {useApolloNetworkStatus: false}
  });

  const [getAccount] = useAccountLazyQuery();

  const events = accountEventsData?.accountEvents.items.slice() || [];
  if (!account)
    return (
      <Card {...cardProps} title="Account details">
        <PropertyList omitEmpty properties={[]} />
      </Card>
    );

  const hasNoteInEvents = loading || events.some((event) => event.object.note === account.note);
  const accountTags = getAccountTags(account);

  const handleDownloadContract = async () => {
    const {data} = await downloadContract({variables: {accountId: account.id}});
    if (data?.downloadContract?.downloadUrl) {
      window.open(data.downloadContract.downloadUrl, '_blank');
    }
  };

  const initialValues: UpdateAccountInput = {
    id: account.id,
    name: account.name,
    status: account.status,
    blocked: account.blocked || false,
    statusReason: account.statusReason,
    statusNote: account.statusNote,
    tags: account?.tags,
    billingPlan: account.billingPlan || BillingPlans.MONEI_X,
    adminEmail: account.adminEmail,
    referralCode: account.referralCode,
    promoCode: account.promoCode,
    refAccountId: account.refAccountId
  };

  const handleSubmit = async (values: UpdateAccountInput) => {
    if (values.refAccountId && values.refAccountId !== account.refAccountId) {
      await getAccount({
        variables: {id: values.refAccountId},
        context: {useApolloNetworkStatus: false}
      });
    }

    if (!hasStatusReason(values.status)) {
      values.statusReason = null;
      values.statusNote = null;
    }

    if (!account.blocked && values.blocked) {
      Modal.confirm({
        title: 'Are you sure?',
        content: 'This will block all transactions for this account',
        okText: 'Confirm',
        cancelText: 'Cancel',
        onOk: async () => {
          await onSubmit(values);
          setEditing(false);
        }
      });
      return;
    }

    await onSubmit(values);
    setEditing(false);
  };

  return (
    <>
      <Card
        {...cardProps}
        title="Account details"
        extra={
          isEditing || !isAdmin ? null : (
            <AnchorLink onClick={() => setEditing(true)}>
              <EditOutlined /> Edit data
            </AnchorLink>
          )
        }>
        {isEditing ? (
          <Formik onSubmit={handleSubmit} initialValues={initialValues}>
            {({setFieldValue}) => (
              <Form {...formProps}>
                <FormItem name="name" label="Name">
                  <Input name="name" />
                </FormItem>
                <FormItem name="status" label="Status">
                  <Select name="status">
                    {Object.values(AccountStatus).map((status) => (
                      <Option key={status} value={status}>
                        {accountStatusLabels[status]}
                      </Option>
                    ))}
                  </Select>
                </FormItem>
                <FormItem
                  name="blocked"
                  label={
                    <Text type="danger" strong>
                      Block transactions
                    </Text>
                  }>
                  <Switch name="blocked" />
                </FormItem>
                {hasStatusReason(account.status) && (
                  <>
                    <FormItem name="statusReason" label="Status reason" hasFeedback={false}>
                      <Select
                        name="statusReason"
                        placeholder="Select a reason"
                        options={enumKeys(statusReasonLabels).map((key) => ({
                          value: key,
                          label: statusReasonLabels[key]
                        }))}
                      />
                    </FormItem>
                    <FormItem
                      name="statusNote"
                      label="Status note"
                      extra={`This message will be shown to the admin of the account: ${account.adminEmail} and it will be sent via email.`}
                      hasFeedback={false}>
                      <TextArea name="statusNote" rows={4} />
                    </FormItem>
                  </>
                )}
                <FormItem name="billingPlan" label="Billing plan">
                  <Select
                    name="billingPlan"
                    options={Object.values(BillingPlans).map((text) => ({
                      value: text,
                      label: text.replace('_', ' ')
                    }))}
                  />
                </FormItem>
                <FormItem name="adminEmail" label="Admin email">
                  <Input name="adminEmail" type="email" />
                </FormItem>
                <FormItem name="refAccountId" label="Main account ID">
                  <Input name="refAccountId" />
                </FormItem>
                <FormItem name="referralCode" label="Affiliate">
                  <Select
                    name="referralCode"
                    loading={affiliatesLoading}
                    showSearch
                    allowClear
                    optionFilterProp="children"
                    onChange={(value) => {
                      if (value === undefined) {
                        setFieldValue('referralCode', null);
                      }
                    }}>
                    {affiliates.map((affiliate) => (
                      <Option key={affiliate.value} value={affiliate.value}>
                        {affiliate.label}
                      </Option>
                    ))}
                  </Select>
                </FormItem>
                <FormItem name="promoCode" label="Promo code">
                  <Input name="promoCode" />
                </FormItem>
                <FormItem name="Tags" label="Tags">
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="Please select"
                    name="tags"
                    options={[
                      ...(accountTagsData?.accountTags.map((tag) => ({
                        label: tag,
                        value: tag
                      })) || []),
                      ...(isAdmin && newTag ? [{label: newTag, value: newTag}] : [])
                    ]}
                    filterOption={(input, option) => {
                      const label = option?.label;
                      if (typeof label === 'string')
                        return label.toLowerCase().includes(input.toLowerCase());
                      return false;
                    }}
                    onSearch={(value) => isAdmin && setNewTag(value)}
                    onBlur={() => setNewTag('')}
                  />
                </FormItem>
                <FormItem name="note" label="Admin notes">
                  <Mentions
                    rows={4}
                    name="note"
                    options={adminUsers}
                    placeholder="You can use @ to mention other admin users"
                  />
                </FormItem>
                <FormItem
                  wrapperCol={{offset: formProps.labelCol?.span, ...formProps.wrapperCol}}
                  name="submit">
                  <Stack>
                    <Button onClick={() => setEditing(false)}>Cancel</Button>
                    <SubmitButton>Submit</SubmitButton>
                  </Stack>
                </FormItem>
              </Form>
            )}
          </Formik>
        ) : (
          <PropertyList
            omitEmpty
            emptyText="No account details provided"
            properties={[
              {
                label: 'ID',
                copyable: {text: account.id},
                value: <Link to={`/payments?accountId=${account.id}`}>{account.id}</Link>
              },
              {
                label: 'Name',
                value: account.name
              },
              {
                label: 'Status',
                value: accountStatusLabels[account.status]
              },
              {
                label: 'Status reason',
                value:
                  account?.statusReason === StatusReason.OTHER
                    ? null
                    : statusReasonLabels[account?.statusReason!]
              },
              {
                label: 'Status note',
                value: account?.statusNote
              },
              {
                label: 'Billing plan',
                value: (account.billingPlan || BillingPlans.MONEI_X).replace('_', ' ')
              },
              {
                label: 'Admin email',
                value: account.adminEmail && (
                  <Link to={'/users/?email=' + encodeURIComponent(account.adminEmail)}>
                    {account.adminEmail}
                  </Link>
                )
              },
              {
                label: 'Affiliate',
                value: isAdmin
                  ? formatAffiliateReferralCode(account.referralCode, affiliates, affiliatesLoading)
                  : null
              },
              {
                label: 'Promo code',
                value: isAdmin ? account.promoCode : null
              },
              {
                label: 'Master account',
                value: masterAccount?.id ? (
                  <Stack>
                    <Link to={'/master-accounts/' + masterAccount?.id}>
                      {masterAccount?.name ?? masterAccount?.id}
                    </Link>
                  </Stack>
                ) : null
              },
              {
                label: 'Original account',
                value: refAccount?.id ? (
                  <Link to={'/accounts/' + refAccount?.id}>
                    {refAccount?.name ?? refAccount?.id}
                  </Link>
                ) : null
              },
              {
                label: 'Contract',
                value:
                  isMaster && account?.contract?.status === ContractStatus.COMPLETED ? (
                    <>
                      Signed: {formatDate(account.contract.signedDate, moment.ISO_8601)}
                      <Divider type="vertical" />
                      <AnchorLink onClick={handleDownloadContract}>
                        {isDownloading ? <LoadingOutlined /> : <DownloadOutlined />} Download
                      </AnchorLink>
                    </>
                  ) : null
              },
              {
                label: 'Approved at',
                value: account.approvedAt && formatDate(account.approvedAt, moment.ISO_8601, true)
              },
              {
                label: 'Tags',
                value: accountTags.length ? (
                  <Stack gutter={1}>
                    {accountTags.map((tag) => (
                      <AccountTag key={tag} accountId={account.id}>
                        {tag}
                      </AccountTag>
                    ))}
                  </Stack>
                ) : null
              },
              {
                label: 'Admin note',
                value:
                  isAdmin && account.note && !hasNoteInEvents ? (
                    <Linkify options={{target: '_blank'}}>{account.note}</Linkify>
                  ) : null
              },
              {
                label: 'Churn reason',
                value: getChurnReason(account)
              }
            ]}
          />
        )}
      </Card>
    </>
  );
};
